import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { FileText, User, Clock, Power, XCircle, ArrowLeft, Share2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { knowledgeBaseService, KnowledgeBase } from '../services/knowledgeBaseService';
import { researchAPI } from '../services/api/researchApi';
import chatService from '../services/chatService';
import { ShareKnowledgeBaseModal } from '../components/knowledgeBase/ShareKnowledgeBaseModal';
import { useDebounce } from '../hooks/useDebounce';
import { canShareKnowledgeBase } from '../utils/knowledgeBasePermissions';
import { useAuth } from '../contexts/AuthContext';
import { ResizableThreeColumnLayout } from '../components/layout/ResizableThreeColumnLayout';
import { FileListPanel } from '../components/knowledgeBase/FileListPanel';
import { ChatPanel } from '../components/knowledgeBase/ChatPanel';
import { ToolboxPanel } from '../components/knowledgeBase/ToolboxPanel';
import { formatDateWithoutYear } from '../utils/dateFormat';
import { KnowledgeBaseAccessDenied } from '../components/auth/AccessDenied';

type FileSortField = 'name' | 'date' | 'size';
type FileSortOrder = 'asc' | 'desc';

const FILE_SORT_FIELD_DEFAULTS: Record<FileSortField, FileSortOrder> = {
  name: 'asc',
  date: 'desc',
  size: 'desc',
};

interface KnowledgeBaseFile {
  id: string;
  file_id?: string; // 文件ID，用于轮询处理状态
  name: string;
  type: 'file' | 'folder';
  author?: string;
  upload_date: string;
  status: 'uploaded' | 'processing' | 'indexed' | 'error';
  size?: number;
  description?: string;
  file_type?: 'pdf' | 'doc' | 'docx' | 'txt' | 'md' | 'xlsx' | 'xls' | 'csv' | 'json' | 'xml';
  page_count?: number;
  chunk_count?: number;
}

const KnowledgeBaseFilesPage: React.FC = () => {
  const { t } = useTranslation();
  const { kbId } = useParams<{ kbId: string }>();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [files, setFiles] = useState<KnowledgeBaseFile[]>([]);
  const [knowledgeBase, setKnowledgeBase] = useState<KnowledgeBase | null>(null);
  const [loading, setLoading] = useState(true);
  const [filesLoading, setFilesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
  const [selectedFilesMap, setSelectedFilesMap] = useState<
    Record<string, { id: string; name: string }>
  >({});
  const [accessDenied, setAccessDenied] = useState(false);
  const [presetPrompt, setPresetPrompt] = useState<string>('');
  const [presetPromptKey, setPresetPromptKey] = useState<number>(0);
  const [loadChatId, setLoadChatId] = useState<string>('');
  const [resetChat, setResetChat] = useState(false);
  const [chatHistory, setChatHistory] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'left' | 'center' | 'right'>('center');
  const [chatHistoryLoading, setChatHistoryLoading] = useState(false);
  const [chatHistoryPage, setChatHistoryPage] = useState(1);
  const [chatHistoryHasMore, setChatHistoryHasMore] = useState(true);
  const [chatHistoryError, setChatHistoryError] = useState<string | null>(null);
  const PAGE_SIZE = 11;
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    pages: 0,
  });

  // Derive sort state from URL params, default to 'name' with 'asc'
  const sortField: FileSortField = (searchParams.get('sort') as FileSortField) || 'name';
  const sortOrder: FileSortOrder =
    (searchParams.get('order') as FileSortOrder) || FILE_SORT_FIELD_DEFAULTS[sortField];

  // Handle sort change with toggle behavior
  const handleSortChange = (field: FileSortField) => {
    const newParams = new URLSearchParams(searchParams);
    let newOrder: FileSortOrder;

    if (sortField === field) {
      // Toggle direction if clicking the same field
      newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // Use default order for new field
      newOrder = FILE_SORT_FIELD_DEFAULTS[field];
    }

    newParams.set('sort', field);
    newParams.set('order', newOrder);
    setSearchParams(newParams);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!kbId) return;

      setLoading(true);
    };

    fetchData();
  }, [kbId]);

  // Handle activate knowledge base
  const handleActivateKB = async () => {
    if (!kbId) return;

    const result = await knowledgeBaseService.activateKnowledgeBase(kbId);
    if (result) {
      toast.success(t('knowledge.activation.success', { name: result.name }));
      setKnowledgeBase(result);
    } else {
      toast.error(t('knowledge.activation.error'));
    }
  };

  // Get file list
  const fetchFiles = async (page: number = pagination.page) => {
    if (!kbId) return;

    setFilesLoading(true);

    try {
      // Map frontend sort field to API sort_by parameter
      const sortByMap: Record<FileSortField, string> = {
        name: 'name',
        date: 'added_at',
        size: 'size_bytes',
      };

      const filesData = await knowledgeBaseService.getKnowledgeBaseFilesPaginated(kbId, {
        search: debouncedSearchQuery,
        page,
        page_size: pagination.per_page,
        sort_by: sortByMap[sortField],
        sort_order: sortOrder,
      });
      setFiles(filesData.items);
      setPagination({
        page: filesData.page,
        per_page: filesData.per_page,
        total: filesData.total,
        pages: filesData.pages,
      });
    } catch (err) {
      console.error('Failed to fetch files:', err);
    } finally {
      setFilesLoading(false);
    }
  };

  // Initial data fetch (only on kbId change)
  useEffect(() => {
    const fetchData = async () => {
      if (!kbId) return;

      setLoading(true);
      setAccessDenied(false);

      // Reset chat history pagination
      setChatHistoryPage(1);
      setChatHistory([]);
      setChatHistoryHasMore(true);
      setChatHistoryError(null);

      try {
        // Fetch knowledge base details
        const kbDetails = await knowledgeBaseService.getKnowledgeBase(kbId);

        if (kbDetails === null) {
          // Check if the error is due to access denied (403/401)
          // The service returns null for any error, so we assume it's access denied
          setAccessDenied(true);
        } else {
          setKnowledgeBase(kbDetails);
          // Fetch chat history after KB is loaded (page 1, not append)
          fetchChatHistory(1, false);
        }
      } catch (err: any) {
        console.error('Failed to fetch KB details:', err);
        // Check for HTTP error status codes for access denied
        if (err.response && (err.response.status === 403 || err.response.status === 401)) {
          setAccessDenied(true);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [kbId]);

  // Fetch files when search/sort changes (doesn't affect main page loading)
  useEffect(() => {
    const fetchFilesData = async () => {
      if (!kbId) return;

      setFilesLoading(true);

      try {
        // Map frontend sort field to API sort_by parameter
        const sortByMap: Record<FileSortField, string> = {
          name: 'name',
          date: 'added_at',
          size: 'size_bytes',
        };

        // Fetch files - always start from page 1 when kbId, debouncedSearchQuery, sort changes
        const filesData = await knowledgeBaseService.getKnowledgeBaseFilesPaginated(kbId, {
          search: debouncedSearchQuery,
          page: 1,
          page_size: 10,
          sort_by: sortByMap[sortField],
          sort_order: sortOrder,
        });
        setFiles(filesData.items);
        setPagination({
          page: filesData.page,
          per_page: filesData.per_page,
          total: filesData.total,
          pages: filesData.pages,
        });
      } catch (err) {
        console.error('Failed to fetch files:', err);
      } finally {
        setFilesLoading(false);
      }
    };

    fetchFilesData();
  }, [kbId, debouncedSearchQuery, sortField, sortOrder]);

  // Handle file selection
  const handleFileSelect = (fileId: string, selected: boolean) => {
    setSelectedFileIds(prev => {
      if (selected) {
        // Add to selected files map
        const file = files.find(f => f.id === fileId);
        if (file) {
          setSelectedFilesMap(prev => ({
            ...prev,
            [fileId]: { id: fileId, name: file.name },
          }));
        }
        return [...prev, fileId];
      } else {
        // Remove from selected files map
        setSelectedFilesMap(prev => {
          const newMap = { ...prev };
          delete newMap[fileId];
          return newMap;
        });
        return prev.filter(id => id !== fileId);
      }
    });
  };

  const handleFileSelectAll = (selected: boolean, selectableFileIds?: string[]) => {
    if (selected) {
      // 如果提供了可选中的文件 ID 列表，只选中这些文件
      if (selectableFileIds) {
        setSelectedFileIds(selectableFileIds);
        // Add to selected files map
        const filesToAdd = files.filter(f => selectableFileIds.includes(f.id));
        setSelectedFilesMap(prev => {
          const newMap = { ...prev };
          filesToAdd.forEach(file => {
            newMap[file.id] = { id: file.id, name: file.name };
          });
          return newMap;
        });
      } else {
        // 否则选中所有文件
        setSelectedFileIds(files.map(f => f.id));
        // Add all to selected files map
        setSelectedFilesMap(prev => {
          const newMap = { ...prev };
          files.forEach(file => {
            newMap[file.id] = { id: file.id, name: file.name };
          });
          return newMap;
        });
      }
    } else {
      setSelectedFileIds([]);
      setSelectedFilesMap({});
    }
  };

  // Handle preset selection from ToolboxPanel
  const handlePresetSelect = (presetType: string) => {
    const prompts: Record<string, string> = {
      summary: t('knowledge.toolbox.prompts.summary', '请总结这个知识库的核心内容'),
      quiz: t('knowledge.toolbox.prompts.quiz', '请基于这个知识库生成一些测试题'),
      flashcards: t('knowledge.toolbox.prompts.flashcards', '请基于这个知识库制作记忆卡片'),
      outline: t('knowledge.toolbox.prompts.outline', '请梳理这个知识库的纲要'),
    };
    // Increment key to force re-mount of input with new value
    setPresetPromptKey(prev => prev + 1);
    setPresetPrompt(prompts[presetType] || '');
    // Auto-switch to Chat tab in mobile view
    setActiveTab('center');
  };

  // Handle preset prompt set callback from ChatPanel
  const handlePresetPromptSet = () => {
    // Clear the preset prompt after it's been set in ChatPanel
    // This allows the same preset to be selected again
    setPresetPrompt('');
  };

  // Handle chat loaded callback
  const handleChatLoaded = () => {
    setLoadChatId('');
  };

  // Handle remove selected file from Input
  const handleRemoveSelectedFile = (fileId: string) => {
    setSelectedFileIds(prev => prev.filter(id => id !== fileId));
    // Remove from selected files map
    setSelectedFilesMap(prev => {
      const newMap = { ...prev };
      delete newMap[fileId];
      return newMap;
    });
  };

  // Handle chat selection from history
  const handleChatSelect = (chatId: string) => {
    setLoadChatId(chatId);
    // Auto-switch to Chat tab in mobile view
    setActiveTab('center');
  };

  // Handle new chat
  const handleNewChat = () => {
    // Reset the chat to start a new one
    setResetChat(true);
    // Reset the flag after a brief delay
    setTimeout(() => setResetChat(false), 100);
    // Auto-switch to Chat tab in mobile view
    setActiveTab('center');
  };

  // Handle chat rename
  const handleChatRename = async (chatId: string, oldTitle: string, newTitle: string) => {
    try {
      await chatService.updateConversation(chatId, { title: newTitle });
      // Update local state
      setChatHistory(prev =>
        prev.map(chat => (chat.id === chatId ? { ...chat, title: newTitle } : chat))
      );
      toast.success(t('chat.renameConversationSuccess', { oldTitle, newTitle }));
      // If the current chat is loaded, update its title too
      if (loadChatId === chatId) {
        // The ChatPanel will need to update its title
        window.location.reload(); // Simple reload to refresh the chat panel
      }
    } catch (error) {
      console.error('Failed to rename chat:', error);
      // Error is handled by global HTTP error handler
    }
  };

  // Handle chat delete
  const handleChatDelete = async (chatId: string, title: string) => {
    try {
      await chatService.deleteConversation(chatId);
      // Update local state
      setChatHistory(prev => prev.filter(chat => chat.id !== chatId));
      toast.success(t('chat.deleteConversationSuccess', { title }));
      // If the deleted chat was currently loaded, reset the chat panel
      if (loadChatId === chatId) {
        handleNewChat();
      }
    } catch (error) {
      console.error('Failed to delete chat:', error);
      // Error is handled by global HTTP error handler
    }
  };

  // Fetch chat history for current knowledge base with pagination
  const fetchChatHistory = async (page: number = 1, append: boolean = false) => {
    if (!kbId) return;

    setChatHistoryLoading(true);
    setChatHistoryError(null);
    try {
      // Use new API endpoint with kb_ids filter
      const response = await researchAPI.getResearchChats({
        page,
        page_size: PAGE_SIZE,
        kb_ids: [kbId],
      });

      const newChats = response.data.items;

      if (append) {
        setChatHistory(prev => [...prev, ...newChats]);
      } else {
        setChatHistory(newChats);
      }

      // Determine if there are more chats
      const hasMore = newChats.length === PAGE_SIZE;
      setChatHistoryHasMore(hasMore);
      setChatHistoryPage(page);
    } catch (error) {
      console.error('Failed to fetch chat history:', error);
      setChatHistoryError(t('knowledge.toolbox.fetchError', 'Failed to load chats'));
      // Error is handled by global HTTP error handler
    } finally {
      setChatHistoryLoading(false);
    }
  };

  // Load more chats when scrolling near bottom
  const handleLoadMoreChats = () => {
    if (chatHistoryHasMore && !chatHistoryLoading) {
      fetchChatHistory(chatHistoryPage + 1, true);
    }
  };

  // Handle new chat creation from ChatPanel
  const handleChatCreated = (chatId: string, title: string) => {
    // Optimistically add new chat to history
    setChatHistory(prev => [
      {
        id: chatId,
        title: title,
        created_at: new Date().toISOString(),
        knowledge_base_ids: [kbId],
      },
      ...prev,
    ]);

    // Auto-select the new chat
    handleChatSelect(chatId);
  };

  // Handle title updates from ChatPanel stream
  const handleChatTitleUpdated = (chatId: string, newTitle: string) => {
    setChatHistory(prev =>
      prev.map(chat => (chat.id === chatId ? { ...chat, title: newTitle } : chat))
    );
  };

  if (loading) {
    return (
      <div className='flex h-screen' style={{ backgroundColor: '#FAFAFA' }}>
        <div className='flex-1 flex items-center justify-center'>
          <div className='text-gray-500'>{t('common.loading')}...</div>
        </div>
      </div>
    );
  }

  // Show access denied state if user doesn't have permission
  if (accessDenied) {
    return <KnowledgeBaseAccessDenied isPrivate={!knowledgeBase?.is_public} />;
  }

  // Show inactive state if KB is not active
  if (knowledgeBase && !knowledgeBase.is_active) {
    return (
      <div className='flex h-screen' style={{ backgroundColor: '#FAFAFA' }}>
        <div className='flex-1 flex flex-col'>
          {/* Header */}
          <div className='bg-gray-100 px-4 sm:px-6 py-4'>
            <div className='flex items-center space-x-2 mb-4 flex-wrap'>
              <Link to='/knowledge' className='text-sm text-gray-600 hover:text-gray-900'>
                {t('knowledge.title')}
              </Link>
              <span className='text-gray-400'>/</span>
              <span className='text-sm text-gray-900 font-medium truncate max-w-[200px] sm:max-w-none'>
                {knowledgeBase.name}
              </span>
              <span className='ml-2 px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full flex items-center gap-1 flex-shrink-0'>
                <XCircle size={10} />
                {t('knowledge.status.inactive')}
              </span>
            </div>
          </div>

          {/* Inactive State Content */}
          <div className='flex-1 flex items-center justify-center p-4 sm:p-6'>
            <div className='bg-white border border-gray-200 rounded-lg p-8 sm:p-12 text-center max-w-lg'>
              <div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                <XCircle className='text-gray-400' size={32} />
              </div>
              <h2 className='text-xl font-semibold text-gray-900 mb-2'>
                {t('knowledge.inactiveState.title')}
              </h2>
              <p className='text-gray-600 mb-6'>{t('knowledge.inactiveState.message')}</p>
              <div className='flex flex-col sm:flex-row items-center justify-center gap-3'>
                <button
                  onClick={handleActivateKB}
                  className='flex items-center space-x-2 bg-blue-500 text-white px-6 py-2.5 rounded-lg hover:bg-blue-600 transition-colors'
                >
                  <Power size={18} />
                  <span>{t('knowledge.actions.activate')}</span>
                </button>
                <Link
                  to='/knowledge'
                  className='flex items-center space-x-2 px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors'
                >
                  <ArrowLeft size={18} />
                  <span>{t('knowledge.inactiveState.backToList')}</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col h-screen' style={{ backgroundColor: '#FAFAFA' }}>
      {/* Top Header */}
      <div
        className='flex-shrink-0 border-b border-gray-200 px-4 sm:px-6 py-4'
        style={{ backgroundColor: '#FAFAFA' }}
      >
        <div className='flex items-center justify-between'>
          {/* Left: Breadcrumbs and Metadata */}
          <div className='flex items-center space-x-4'>
            {/* Breadcrumbs */}
            <div className='flex items-center space-x-2'>
              <Link to='/knowledge' className='text-sm text-gray-600 hover:text-gray-900'>
                {t('knowledge.title')}
              </Link>
              <span className='text-gray-400'>/</span>
              <span className='text-sm text-gray-900 font-medium truncate max-w-[200px] sm:max-w-none'>
                {knowledgeBase?.name || t('knowledge.defaultKnowledgeBase')}
              </span>
            </div>

            {/* Metadata - Desktop */}
            <div className='hidden sm:flex items-center space-x-4 text-sm text-gray-600'>
              {knowledgeBase?.owner && (
                <div className='flex items-center space-x-2'>
                  <div className='w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0'>
                    <User className='text-blue-600' size={14} />
                  </div>
                  <span className='truncate'>{knowledgeBase.owner.full_name}</span>
                </div>
              )}
              <div className='flex items-center space-x-2'>
                <FileText className='text-gray-400 flex-shrink-0' size={16} />
                <span>
                  {pagination.total > 0
                    ? pagination.total
                    : knowledgeBase?.document_count || files.length}{' '}
                  {t('knowledge.files.filesCount')}
                </span>
              </div>
              <div className='flex items-center space-x-2'>
                <Clock className='text-gray-400 flex-shrink-0' size={16} />
                <span>
                  {t('knowledge.files.createdAt')}{' '}
                  {formatDateWithoutYear(knowledgeBase?.created_at || '')} |{' '}
                  {t('knowledge.files.updatedAt')}{' '}
                  {formatDateWithoutYear(knowledgeBase?.updated_at || '')}
                </span>
              </div>
            </div>
          </div>

          {/* Right: Share Button */}
          {knowledgeBase && canShareKnowledgeBase(knowledgeBase, user?.id) && (
            <button
              onClick={() => setShowShareModal(true)}
              className='flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors'
            >
              <Share2 size={18} />
              <span className='hidden sm:inline'>{t('knowledge.files.share')}</span>
            </button>
          )}
        </div>
      </div>

      {/* Three Column Layout */}
      <div className='flex-1 overflow-hidden'>
        <ResizableThreeColumnLayout
          leftPanel={
            <FileListPanel
              files={files}
              loading={filesLoading}
              selectedFileIds={selectedFileIds}
              onFileSelect={handleFileSelect}
              onFileSelectAll={handleFileSelectAll}
              onSortChange={handleSortChange}
              onSearchChange={setSearchQuery}
              searchQuery={searchQuery}
              sortField={sortField}
              sortOrder={sortOrder}
              onRefresh={fetchFiles}
              knowledgeBase={knowledgeBase}
              pagination={pagination}
              onPageChange={fetchFiles}
            />
          }
          centerPanel={
            <ChatPanel
              selectedFileIds={selectedFileIds}
              files={Object.values(selectedFilesMap)}
              knowledgeBaseId={kbId}
              knowledgeBaseName={knowledgeBase?.name}
              presetPrompt={presetPrompt}
              presetPromptKey={presetPromptKey}
              loadChatId={loadChatId}
              resetChat={resetChat}
              onChatLoaded={handleChatLoaded}
              onRemoveSelectedFile={handleRemoveSelectedFile}
              onPresetPromptSet={handlePresetPromptSet}
              onChatCreated={handleChatCreated}
              onChatTitleUpdated={handleChatTitleUpdated}
            />
          }
          rightPanel={
            <ToolboxPanel
              outputs={[]}
              loading={false}
              onPresetSelect={handlePresetSelect}
              chatHistory={chatHistory}
              chatHistoryLoading={chatHistoryLoading}
              chatHistoryHasMore={chatHistoryHasMore}
              chatHistoryError={chatHistoryError}
              onChatSelect={handleChatSelect}
              onNewChat={handleNewChat}
              onChatRename={handleChatRename}
              onChatDelete={handleChatDelete}
              onLoadMoreChats={handleLoadMoreChats}
              knowledgeBaseId={kbId}
            />
          }
          files={files.map(f => ({ id: f.id, name: f.name, type: f.file_type }))}
          outputs={[]}
          leftPanelMinWidth={250}
          centerPanelMinWidth={350}
          rightPanelMinWidth={280}
          leftPanelDefaultWidth={320}
          rightPanelDefaultWidth={320}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>

      {/* Share Modal */}
      {showShareModal && kbId && (
        <ShareKnowledgeBaseModal
          open={showShareModal}
          onClose={() => setShowShareModal(false)}
          knowledgeBaseId={kbId}
          knowledgeBaseName={knowledgeBase?.name}
          owner={knowledgeBase?.owner}
        />
      )}
    </div>
  );
};

export default KnowledgeBaseFilesPage;
